import type { BaseSyntheticEvent } from "react";
import type { UseControllerProps, UseFormReturn } from "react-hook-form";
import {
  useWatchTripForm as useApacWatchTripForm,
  type PriceWatchForm,
} from "@apac/price-watch";
import type { FlightKey } from "@b2bportal/air-price-watch-api";
import { trackEvent } from "@hopper-b2b/api";
import { ADDED_WATCH } from "@hopper-b2b/types";

export interface WatchTripRequest {
  email: string;
  nonstop: boolean;
  noBasicFare: boolean;
}
export type WatchTripResponse = unknown;

export interface UseWatchTripFormReturn {
  context: {
    form: UseFormReturn<PriceWatchForm>;
  };
  handlers: {
    onWatchTripSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  };
  componentProps: Record<
    "emailInputProps" | "nonstopInputProps" | "noBasicFareInputProps",
    UseControllerProps<PriceWatchForm, keyof PriceWatchForm>
  >;
}

export interface UseWatchTripFormProps {
  defaultEmail?: string;
  flightKey: FlightKey;
  onSubmit?: (request: PriceWatchForm) => unknown;
  onSuccess?: (response: WatchTripResponse) => unknown;
  onError?: (error: unknown) => unknown;
}

export const useWatchTripForm = ({
  defaultEmail = "",
  flightKey,
  onSubmit,
  onSuccess,
  onError,
}: UseWatchTripFormProps): UseWatchTripFormReturn => {
  const {
    context: { form, formControls },
    handlers: { watch },
  } = useApacWatchTripForm({ flightKey, email: defaultEmail });

  return {
    context: { form },
    handlers: {
      onWatchTripSubmit: form.handleSubmit(async (request) => {
        onSubmit?.(request);
        try {
          const response = await watch();
          trackEvent({
            eventName: ADDED_WATCH,
            properties: {},
          });
          await onSuccess?.(response);

          return response;
        } catch (error) {
          console.error("Error in watch trip request: ", error);
          onError?.(error);
        }
      }),
    },
    componentProps: {
      emailInputProps: formControls.email,
      nonstopInputProps: formControls.nonstop,
      noBasicFareInputProps: formControls.noLCC,
    },
  };
};
